/**
 * paradisesociety — product card reveal (once per card).
 *
 * Premium FOUC-free hide (CSS html.js) + once-guards kept.
 *
 * AJAX integration (theme.liquid SPA):
 * Navigation swaps #MainContent via innerHTML and dispatches `ajax:loaded`.
 * Reveal must re-scan new cards on that event (and Theme Editor section:load).
 * Facets/recommendations also inject cards under #MainContent — covered by a
 * scoped MutationObserver on #MainContent only (not document-wide).
 */
(function () {
  const CARD_SELECTOR = '.product-card-wrapper';
  const VISIBLE_CLASS = 'fkx-card-reveal--visible';
  const DONE_CLASS = 'fkx-card-reveal--done';
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window);
  const REVEAL_DURATION_MS = 340;
  const STAGGER_MS = 20;

  const scheduled = new WeakSet();
  const completed = new WeakSet();
  let observer = null;
  let mainObserver = null;
  let mainObservedEl = null;
  let scanQueued = false;
  let staggerIndex = 0;
  let staggerResetTimer = 0;

  function finalizeCard(card) {
    if (completed.has(card) || card.classList.contains(DONE_CLASS)) return;
    completed.add(card);
    scheduled.add(card);
    card.classList.add(VISIBLE_CLASS, DONE_CLASS);
  }

  function revealCard(card, delayMs) {
    if (completed.has(card) || scheduled.has(card) || card.classList.contains(DONE_CLASS)) return;
    scheduled.add(card);

    if (REDUCED_MOTION) {
      card.classList.add(VISIBLE_CLASS, DONE_CLASS);
      completed.add(card);
      return;
    }

    const start = () => {
      if (completed.has(card) || card.classList.contains(VISIBLE_CLASS)) return;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (completed.has(card) || card.classList.contains(VISIBLE_CLASS)) return;
          card.classList.add(VISIBLE_CLASS);
        });
      });

      window.setTimeout(() => finalizeCard(card), REVEAL_DURATION_MS + 48);
    };

    if (delayMs > 0) {
      window.setTimeout(start, delayMs);
    } else {
      start();
    }
  }

  function nextStaggerDelay() {
    const delay = staggerIndex * STAGGER_MS;
    staggerIndex += 1;
    window.clearTimeout(staggerResetTimer);
    staggerResetTimer = window.setTimeout(() => {
      staggerIndex = 0;
    }, STAGGER_MS * 10);
    return delay;
  }

  function resetStagger() {
    staggerIndex = 0;
    window.clearTimeout(staggerResetTimer);
  }

  function ensureObserver() {
    if (observer || REDUCED_MOTION) return;

    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const card = entry.target;
          observer.unobserve(card);
          revealCard(card, nextStaggerDelay());
        });
      },
      {
        // Earlier start: avoid opacity:0 while card is already deep in-viewport
        // (measured enterTop ~0.82vh with threshold 0.12 + rootMargin -6%).
        threshold: 0.02,
        rootMargin: '0px 0px 10% 0px',
      }
    );
  }

  function observeCards(root) {
    const scope = root && root.querySelectorAll ? root : document;
    const cards = scope.querySelectorAll(CARD_SELECTOR);
    if (!cards.length) return;

    if (REDUCED_MOTION) {
      cards.forEach((card) => {
        card.classList.add(VISIBLE_CLASS, DONE_CLASS);
        completed.add(card);
        scheduled.add(card);
      });
      return;
    }

    ensureObserver();
    document.documentElement.classList.add('fkx-card-reveal-ready');
    cards.forEach((card) => {
      if (completed.has(card) || scheduled.has(card) || card.classList.contains(DONE_CLASS)) return;
      if (card.classList.contains(VISIBLE_CLASS)) {
        finalizeCard(card);
        return;
      }
      observer.observe(card);
    });
  }

  function queueObserve(root) {
    if (scanQueued) return;
    scanQueued = true;
    requestAnimationFrame(() => {
      scanQueued = false;
      observeCards(root || document.getElementById('MainContent') || document);
    });
  }

  function armMainContentObserver() {
    const main = document.getElementById('MainContent');
    if (!main) return;

    // Element identity is stable across AJAX swaps (innerHTML only).
    if (mainObserver && mainObservedEl === main) return;

    if (mainObserver) {
      mainObserver.disconnect();
      mainObserver = null;
      mainObservedEl = null;
    }

    mainObserver = new MutationObserver((mutations) => {
      for (let i = 0; i < mutations.length; i++) {
        const nodes = mutations[i].addedNodes;
        for (let j = 0; j < nodes.length; j++) {
          const node = nodes[j];
          if (node.nodeType !== 1) continue;
          if (node.matches?.(CARD_SELECTOR) || node.querySelector?.(CARD_SELECTOR)) {
            queueObserve(main);
            return;
          }
        }
      }
    });

    mainObserver.observe(main, { childList: true, subtree: true });
    mainObservedEl = main;
  }

  function onAjaxLoaded() {
    resetStagger();
    armMainContentObserver();
    queueObserve(document.getElementById('MainContent') || document);
  }

  function boot() {
    armMainContentObserver();
    observeCards(document.getElementById('MainContent') || document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  // theme.liquid SPA navigation after #MainContent swap
  document.addEventListener('ajax:loaded', onAjaxLoaded);
  document.addEventListener('ajax:before', () => {
    if (observer) observer.disconnect();
    resetStagger();
  });

  document.addEventListener('shopify:section:load', (event) => {
    resetStagger();
    queueObserve(event.target);
  });
})();
